import { z } from 'zod';
import { DispatchStatus, TripStatus, TripStopStatus } from '../enums.js';
import type { LatLon } from '../geo.js';

const isoDateTime = z.string().datetime({ offset: true });
const lat = z.number().min(-90).max(90);
const lon = z.number().min(-180).max(180);

/** A trip endpoint: either a saved Place or a free point (text + coordinates). */
export const TripPointSchema = z
  .object({
    placeId: z.string().uuid().nullable().optional(),
    text: z.string().max(200).nullable().optional(),
    lat: lat.nullable().optional(),
    lon: lon.nullable().optional(),
  })
  .refine((p) => !!p.placeId || (p.lat != null && p.lon != null) || !!p.text, { message: 'placeId, coordinates or text required' });
export type TripPoint = z.infer<typeof TripPointSchema>;

export const TripStopInputSchema = z.object({
  placeId: z.string().uuid().nullable().optional(),
  customerId: z.string().uuid().nullable().optional(),
  addressText: z.string().max(200).nullable().optional(),
  lat: lat.nullable().optional(),
  lon: lon.nullable().optional(),
  plannedAt: isoDateTime.nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
});
export type TripStopInput = z.infer<typeof TripStopInputSchema>;

export const CreateTripSchema = z.object({
  vehicleId: z.string().uuid().nullable().optional(),
  driverId: z.string().uuid().nullable().optional(),
  customerId: z.string().uuid().nullable().optional(),
  shipmentOrderNo: z.string().max(60).nullable().optional(),
  cargoType: z.string().max(60).nullable().optional(),
  cargoNotes: z.string().max(500).nullable().optional(),
  origin: TripPointSchema.optional(),
  dest: TripPointSchema.optional(),
  scheduledAt: isoDateTime.nullable().optional(),
  dueAt: isoDateTime.nullable().optional(),
  notes: z.string().max(1000).nullable().optional(),
  stops: z.array(TripStopInputSchema).max(50).optional(),
  /** Create + dispatch to the driver in one call. Requires vehicleId + driverId. */
  dispatch: z.boolean().default(false),
});
export type CreateTrip = z.infer<typeof CreateTripSchema>;

export const UpdateTripSchema = CreateTripSchema.omit({ dispatch: true }).partial();
export type UpdateTrip = z.infer<typeof UpdateTripSchema>;

export const DispatchTripSchema = z.object({
  vehicleId: z.string().uuid().optional(),
  driverId: z.string().uuid().optional(),
  message: z.string().max(300).optional(),
});
export type DispatchTrip = z.infer<typeof DispatchTripSchema>;

export const RejectTripSchema = z.object({ reason: z.string().min(2).max(300) });
export type RejectTrip = z.infer<typeof RejectTripSchema>;

export const CancelTripSchema = z.object({ reason: z.string().max(300).optional() });
export type CancelTrip = z.infer<typeof CancelTripSchema>;

export const StartTripSchema = z.object({
  odometerKm: z.number().min(0).max(10_000_000).nullable().optional(),
  lat: lat.nullable().optional(),
  lon: lon.nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
});
export type StartTrip = z.infer<typeof StartTripSchema>;

/** Driver-initiated trip: created and started in one go on the driver's current vehicle. */
export const DriverStartTripSchema = StartTripSchema.extend({
  dest: TripPointSchema.optional(),
  customerId: z.string().uuid().nullable().optional(),
  shipmentOrderNo: z.string().max(60).nullable().optional(),
  cargoType: z.string().max(60).nullable().optional(),
});
export type DriverStartTrip = z.infer<typeof DriverStartTripSchema>;

export const EndTripSchema = z.object({
  odometerKm: z.number().min(0).max(10_000_000).nullable().optional(),
  lat: lat.nullable().optional(),
  lon: lon.nullable().optional(),
  podFileId: z.string().uuid().nullable().optional(),
  podSignatureFileId: z.string().uuid().nullable().optional(),
  podReceiverName: z.string().max(120).nullable().optional(),
  endNotes: z.string().max(1000).nullable().optional(),
});
export type EndTrip = z.infer<typeof EndTripSchema>;

export const TripsQuerySchema = z.object({
  status: z.enum(TripStatus).optional(),
  dispatchStatus: z.enum(DispatchStatus).optional(),
  vehicleId: z.string().uuid().optional(),
  driverId: z.string().uuid().optional(),
  customerId: z.string().uuid().optional(),
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
});
export type TripsQuery = z.infer<typeof TripsQuerySchema>;

/** Driver-side list scope. */
export const DriverTripsScope = ['inbox', 'active', 'upcoming', 'completed', 'all'] as const;
export type DriverTripsScope = (typeof DriverTripsScope)[number];

/** Which transitions are allowed from each status (server is the source of truth; UI mirrors it). */
export const TRIP_TRANSITIONS: Record<TripStatus, readonly ('dispatch' | 'start' | 'pause' | 'resume' | 'end' | 'cancel' | 'update')[]> = {
  DRAFT: ['dispatch', 'start', 'cancel', 'update'],
  SCHEDULED: ['dispatch', 'start', 'cancel', 'update'],
  IN_PROGRESS: ['pause', 'end', 'cancel'],
  PAUSED: ['resume', 'end', 'cancel'],
  COMPLETED: [],
  CANCELLED: [],
};

// ─────────────────────────── live progress ───────────────────────────

/** A point the trip must pass: origin, each stop in order, then destination. */
export interface TripWaypoint {
  kind: 'origin' | 'stop' | 'dest';
  /** Stop sequence (null for origin/dest). */
  seq: number | null;
  stopId: string | null;
  label: string | null;
  lat: number;
  lon: number;
  status: TripStopStatus | null;
  plannedAt: string | null;
  arrivedAt: string | null;
  departedAt: string | null;
  /** Already passed (origin once started, departed/skipped stops, dest once completed). */
  done: boolean;
}

/**
 * Live progress of a trip for the map panel: where it goes, how far it got and when it should arrive.
 * Distances are road distances when a routing provider is configured, otherwise great-circle × a road factor.
 */
export interface TripProgress {
  tripId: string;
  number: string;
  status: TripStatus;
  dispatchStatus: DispatchStatus;
  vehicleId: string | null;
  driver: { id: string; fullName: string; phone: string } | null;
  scheduledAt: string | null;
  dueAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  waypoints: TripWaypoint[];
  /** Vehicle position the estimate was computed from (null when the vehicle has no live fix). */
  current: { lat: number; lon: number; speedKph: number | null; at: string | null } | null;
  /** Total planned length origin → stops → dest. */
  plannedKm: number | null;
  /** Distance actually driven since start (positions tagged with the trip). */
  drivenKm: number | null;
  elapsedSec: number | null;
  /** From the current position through the remaining waypoints to the destination. */
  remainingKm: number | null;
  remainingSec: number | null;
  etaAt: string | null;
  /** Speed used for the ETA (trip average when meaningful, else current speed, else a default). */
  etaSpeedKph: number | null;
  /** Minutes the ETA is past `dueAt` (negative = early). Null without a due time or ETA. */
  delayMin: number | null;
  /** 0..100, driven / (driven + remaining). */
  progressPct: number | null;
  nextWaypoint: TripWaypoint | null;
  /** Polyline of the remaining path (current → remaining waypoints), for drawing on the map. */
  remainingPath: { lat: number; lon: number }[];
  routeSource: 'osrm' | 'straight';
}

/** A running trip drawn on the live map for every vehicle at once: where it has been and where it still goes. */
export interface LiveTrip {
  tripId: string;
  number: string;
  status: TripStatus;
  vehicleId: string;
  plateAr: string;
  waypoints: TripWaypoint[];
  /** Path driven so far (downsampled primary positions since the start). */
  driven: LatLon[];
  /** Polyline from the current position through the pending waypoints to the destination. */
  remainingPath: LatLon[];
  progressPct: number | null;
  etaAt: string | null;
}

/** Resolution of "which trip is this vehicle on right now". */
export interface VehicleActiveTrip {
  kind: 'active' | 'upcoming' | null;
  tripId: string | null;
}
